import React from 'react';
import { displayPriceFormat } from '../../../utils';
import { useAppSelector } from '../../../redux/hook';
import {
  DISHOP_GREEN,
  MODIFY_ORDER_TYPE_PROMOTION,
  ORDER_TYPE_DELIVERY,
  PROMOTION_DELIVERY_FREE,
  PROMOTION_PERCENTAGE,
  UNIT,
} from '../../../constants';
import OrderPromotion from './OrderPromotion';
import Spinner from '../../Spinner';
import SkeletonRow from '../../Skeleton/SkeletonRow.view';
import { getTitleFromOffer } from '../../ThresholdMarkertingProgress/ThresholdMarketingProgress.service';
import DashedLine from '../../DashedLine/DashedLine';

type OrderTotalProps = {
  commande?: any;
  promotion?: any;
  showFees: boolean;
  isLoading?: boolean;
  marketingCampaignPromoAmount?: number;
  totalPriceOrder: number;
  isOrderFreeDelivery?: boolean;
  managementFee: number;
};

export default function OrderTotal({
  commande,
  promotion,
  showFees,
  isLoading = false,
  marketingCampaignPromoAmount,
  totalPriceOrder,
  isOrderFreeDelivery = false,
  managementFee = 0,
}: OrderTotalProps) {
  const {
    address = {},
    orderType,
    freeDelivery,
  } = useAppSelector((state) => state.pendingOrderReducer);

  const { thresholdOffer } = useAppSelector((state) => state.offerReducer);

  const userServicePrice = address?.deliveryPrice ?? 0;

  const displayPriceAsync = (price: number) =>
    isLoading ? <Spinner /> : displayPriceFormat(price, false);
  const deliveryIsFree =
    (promotion && promotion.type === PROMOTION_DELIVERY_FREE) ||
    freeDelivery ||
    isOrderFreeDelivery;

  const isClickAndCollectPromo =
    promotion?.reconversionType ===
    MODIFY_ORDER_TYPE_PROMOTION.CLICK_AND_COLLECT;

  const hasPromotion =
    (promotion && !isClickAndCollectPromo) || !!marketingCampaignPromoAmount;
  const isDeliveryOrder = orderType === ORDER_TYPE_DELIVERY;

  const hasFee =
    (showFees && (isDeliveryOrder || managementFee > 0)) ||
    isClickAndCollectPromo;

  const renderPromotion = (promo: any) => {
    if (!promo) return null;

    const isPercentage =
      typeof promo.type === 'string' &&
      promo.type.includes(PROMOTION_PERCENTAGE);

    const promoType =
      isPercentage && !isLoading
        ? promo.type
        : !isLoading && typeof promo.type === 'number'
          ? displayPriceFormat(promo.type)
          : null;

    return (
      promoType && (
        <OrderPromotion
          promotion={promo}
          commande={commande}
          promotionType={promoType}
          prefix='-'
          isLoading={isLoading}
        />
      )
    );
  };

  return (
    <>
      {hasPromotion && (
        <>
          {promotion && !isClickAndCollectPromo && renderPromotion(promotion)}

          {!!marketingCampaignPromoAmount && (
            <SkeletonRow
              label={
                <div style={{ lineHeight: 1.2 }}>
                  {commande
                    ? 'Campagne Marketing'
                    : getTitleFromOffer(thresholdOffer)}
                </div>
              }
              isLoading={isLoading}
              amount={`- ${displayPriceFormat(
                marketingCampaignPromoAmount,
                false,
                true
              )}`}
              style={{ color: DISHOP_GREEN }}
            />
          )}
          <DashedLine />
        </>
      )}

      {hasFee && (
        <>
          {((showFees && isDeliveryOrder) || isClickAndCollectPromo) && (
            <SkeletonRow
              label='Livraison'
              amount={displayPriceAsync(userServicePrice)}
              isLoading={isLoading}
              style={{ color: 'unset' }}
            />
          )}

          {showFees && managementFee > 0 && (
            <SkeletonRow
              label='Frais et Services'
              amount={displayPriceAsync(managementFee)}
              isLoading={isLoading}
            />
          )}

          <DashedLine />
        </>
      )}

      {deliveryIsFree && (
        <>
          <OrderPromotion
            promotion={promotion}
            commande={commande}
            promotionType={displayPriceAsync(userServicePrice)}
            prefix='-'
            isLoading={isLoading}
            freeDelivery={deliveryIsFree}
          />
          <DashedLine />
        </>
      )}

      {promotion && isClickAndCollectPromo && (
        <>
          {renderPromotion(promotion)}
          <DashedLine />
        </>
      )}

      <div
        className='row'
        style={{ paddingTop: 5 }}
      >
        <SkeletonRow
          label={<h4>Total</h4>}
          amount={
            <div style={{ fontSize: UNIT * 1.3, fontWeight: 'bold' }}>
              {displayPriceAsync(totalPriceOrder)}
            </div>
          }
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
